import { createElement, type ReactElement } from 'react';

import type { TabsProps } from '@bookora/ui';

/**
 * Tour anchors — the ONLY file in the panel allowed to contain a `data-tour`
 * literal (`anchors.test.ts` enforces it).
 *
 * A tour step points at an anchor id, never at a CSS selector, so a layout
 * refactor that moves a button keeps its tour working as long as the
 * `tourAnchor()` spread moves with it. Nav items are anchored by section key
 * through `navAnchor()`, on the menu `<li>` (never on the label span).
 *
 * Ids are declared here BEFORE the screen places them (the 2026-09 guidance
 * work reserved them all at once); until placed, an id sits in
 * `PENDING_ANCHORS` in `anchors.test.ts` with the stream that places it. A
 * step pointing at an absent anchor is simply skipped at runtime.
 */

export const TOUR_ANCHORS = [
  // Shell
  'shell.nav',
  'shell.menu-button',
  'shell.search',
  'shell.bell',
  'shell.help',
  'shell.user-menu',
  'page.tour-chip',
  // Calendar
  'calendar.nav',
  'calendar.filters',
  'calendar.filters-compact',
  'calendar.board',
  'calendar.tab-requests',
  'calendar.board-empty',
  'calendar.tab-waitlist',
  // Appointment form (inline tour inside the booking dialog)
  'appointment.patient',
  'appointment.services',
  'appointment.datetime',
  'appointment.doctor',
  // Session form (inline tour inside the drawer)
  'session.procedure',
  'session.status',
  'session.consumption',
  'session.submit',
  'session.price',
  // Session page (a planned session's finalise)
  'session.finalize',
  // Patient file
  'patient.alerts',
  'patient.actions',
  'patient.timeline',
  'patient.consents',
  'patient.financial',
  'patient.next-steps',
  // Patient list
  'patients.create',
  'patients.import',
  'patients.filters',
  // Dashboard
  'dashboard.actions',
  'dashboard.today',
  'dashboard.followups',
  'dashboard.suggestions',
  // Packages
  'packages.tab-bundles',
  'packages.tab-sales',
  'packages.add',
  // Procedures (tariff)
  'procedures.tab-tariff',
  'procedures.add',
  'procedures.custom',
  // Staff
  'staff.invite',
  'staff.team',
  'staff.tab-invites',
  'staff.tab-roles',
  // Working hours (settings › ساعت کاری)
  'hours.bulk',
  'hours.edit',
  // Inventory
  'inventory.tabs',
  'inventory.tab-alerts',
  'inventory.receive',
  // CRM
  'crm.tabs',
  'crm.tab-leads',
  'crm.tab-segments',
  'crm.tab-campaigns',
  'crm.tab-loyalty',
  'crm.tab-surveys',
  // «بینش‌ها»
  'insights.period',
  'insights.funnel',
  // Accounting
  'accounting.tabs',
  'accounting.period',
  'accounting.actions',
  // «ثبت اطلاعات از روی عکس»
  'import.entry',
  'import.kind-picker',
  'import.capture',
  'import.start',
  'import.review.toolbar',
  'import.review.unit',
  'import.review.next-flag',
  'import.review.flagged-cell',
  'import.review.commit',
  'import.review.history',
  'import.review.count-diff',
  'import.review.group',
  'import.viewer',
  // Pricing build — declared by WS-0, placed by the panel streams (see
  // `pending-anchors/*.ts`, which each stream empties as it places them).
  // Subscription (panel-billing)
  'subscription.model-switch',
  'subscription.calculator',
  // «نتایج و صورت‌حساب» (panel-billing)
  'results.meter',
  'results.charges',
  'results.dispute',
  'results.wallet',
  'results.alert-budget',
  // «شعبه‌ها» (panel-branches-ops)
  'branches.add',
  'branches.staff',
  'branches.hours',
  'branches.switcher',
] as const;

export type TourAnchorId = (typeof TOUR_ANCHORS)[number];

/** Spread onto the anchored element: `<Button {...tourAnchor('shell.search')} />`. */
export function tourAnchor(id: TourAnchorId): { 'data-tour': TourAnchorId } {
  return { 'data-tour': id };
}

/** The anchor of a sidebar section, spread onto its menu item (lands on the `<li>`). */
export function navAnchor(sectionKey: string): { 'data-tour': `nav.${string}` } {
  return { 'data-tour': `nav.${sectionKey}` };
}

type RenderTabBar = NonNullable<TabsProps['renderTabBar']>;

/**
 * `renderTabBar` for a `<Tabs>` whose tour step points at the tab STRIP: the
 * anchor goes on the bar, not around the whole `Tabs` — which is the whole
 * page, and a spotlight on the whole page highlights nothing.
 *
 *   <Tabs renderTabBar={tabBarAnchor('crm.tabs')} … />
 */
export function tabBarAnchor(id: TourAnchorId): RenderTabBar {
  function AnchoredTabBar(
    props: Parameters<RenderTabBar>[0],
    DefaultTabBar: Parameters<RenderTabBar>[1],
  ): ReactElement {
    return createElement('div', tourAnchor(id), createElement(DefaultTabBar, props));
  }
  return AnchoredTabBar;
}
